/**
 * Phase I — Canonical date/currency formatters.
 *
 * Single source of truth for every German/Austrian locale-formatted
 * number and date in the app. Replaces ad-hoc `toLocaleString` and
 * `toFixed(2)` calls scattered across pages.
 *
 *   fmtDate("2026-05-26")          → "26.05.2026"
 *   fmtDateTime("2026-05-26T13:..")→ "26.05.2026 13:45"
 *   fmtCur(1234.5)                 → "€ 1.234,50"
 *   fmtCurAmount(1234.5)           → "1.234,50"     (no symbol)
 *   fmtNum(1234.567)               → "1.234,57"     (general number)
 *   fmtInt(12345)                  → "12.345"
 *
 * Behaviour: every formatter accepts null / undefined / "" / NaN and
 * returns a stable em-dash ("—") so consumers never need null-guards.
 */

const LOCALE = "de-AT";

function _hasValue(v) {
  if (v === null || v === undefined) return false;
  if (v === "") return false;
  if (typeof v === "number" && Number.isNaN(v)) return false;
  return true;
}

function _toDate(v) {
  if (v instanceof Date) return v;
  if (typeof v === "string") {
    // Mongo-style "2026-05-26" → midnight local. ISO datetime → as-is.
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === "number") return new Date(v);
  return null;
}

/** "2026-05-26" or Date → "26.05.2026". Falsy → "—". */
export function fmtDate(value) {
  if (!_hasValue(value)) return "—";
  const d = _toDate(value);
  if (!d) return String(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}.${mm}.${yy}`;
}

/** Datetime → "26.05.2026 13:45". Falsy → "—". */
export function fmtDateTime(value) {
  if (!_hasValue(value)) return "—";
  const d = _toDate(value);
  if (!d) return String(value);
  const date = fmtDate(d);
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${date} ${hh}:${mi}`;
}

/** Plain time "13:45" from Date / ISO. Falsy → "—". */
export function fmtTime(value) {
  if (!_hasValue(value)) return "—";
  const d = _toDate(value);
  if (!d) return String(value);
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mi}`;
}

/** Currency with € prefix + thin-space separator: "€ 1.234,50". */
export function fmtCur(value) {
  if (!_hasValue(value)) return "€ 0,00";
  const n = Number(value) || 0;
  const formatted = n.toLocaleString(LOCALE, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return `€ ${formatted}`;
}

/** Currency without symbol (caller adds €/EUR): "1.234,50". */
export function fmtCurAmount(value) {
  if (!_hasValue(value)) return "0,00";
  return (Number(value) || 0).toLocaleString(LOCALE, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

/** General decimal: "1.234,57" (rounded). */
export function fmtNum(value, fractionDigits = 2) {
  if (!_hasValue(value)) return "—";
  return (Number(value) || 0).toLocaleString(LOCALE, {
    minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits,
  });
}

/** Integer: "12.345". */
export function fmtInt(value) {
  if (!_hasValue(value)) return "—";
  return (Number(value) || 0).toLocaleString(LOCALE, {
    maximumFractionDigits: 0,
  });
}

/** Bytes → KB/MB/GB with 2 decimals. */
export function fmtBytes(n) {
  if (!_hasValue(n) || n <= 0) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
